import React, { useState, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';

const tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';

const TimetableSetup = ({ onSetupComplete }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [setupData, setSetupData] = useState({
    mode: 'weekday', // 'weekday' or 'cycle'
    dayPeriods: 14,
    periodPatterns: []
  });

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Initialize default period patterns based on your requirements
  useEffect(() => {
    if (setupData.dayPeriods > 0 && setupData.periodPatterns.length === 0) {
      initializeDefaultPatterns();
    }
  }, [setupData.dayPeriods]);

  const initializeDefaultPatterns = () => {
    const patterns = [];
    for (let i = 1; i <= setupData.dayPeriods; i++) {
      let periodNode = '';
      let timeSlot = '';
      
      if (i === 1) {
        periodNode = '班主任節';
        timeSlot = '7:45-8:10';
      } else if (i === 2) {
        periodNode = 'Period 1';
        timeSlot = '8:10-8:45';
      } else if (i === 3) {
        periodNode = 'Period 2';
        timeSlot = '8:45-9:20';
      } else if (i === 4) {
        periodNode = 'Period 3';
        timeSlot = '9:20-9:55';
      } else if (i === 5) {
        periodNode = '小息';
        timeSlot = '9:55-10:15';
      } else if (i === 6) {
        periodNode = 'Period 4';
        timeSlot = '10:15-10:50';
      } else if (i === 7) {
        periodNode = 'Period 5';
        timeSlot = '10:50-11:25';
      } else if (i === 8) {
        periodNode = '小息';
        timeSlot = '11:25-11:30';
      } else if (i === 9) {
        periodNode = 'Period 6';
        timeSlot = '11:30-12:05';
      } else if (i === 10) {
        periodNode = 'Period 7';
        timeSlot = '12:05-12:40';
      } else if (i === 11) {
        periodNode = '午膳';
        timeSlot = '12:40-1:50';
      } else if (i === 12) {
        periodNode = 'Period 8';
        timeSlot = '1:50-2:25';
      } else if (i === 13) {
        periodNode = 'Period 9';
        timeSlot = '2:25-3:00';
      } else if (i === 14) {
        periodNode = '班主任節';
        timeSlot = '3:00-3:35';
      } else {
        periodNode = `Period ${i - 2}`;
        timeSlot = 'TBD';
      }
      
      patterns.push({
        period_sequence: i,
        period_node: periodNode,
        time_slot: timeSlot
      });
    }
    
    setSetupData(prev => ({
      ...prev,
      periodPatterns: patterns
    }));
  };

  const handleModeChange = (mode) => {
    setSetupData(prev => ({
      ...prev,
      mode: mode
    }));
  };

  const handleDayPeriodsChange = (periods) => {
    const numPeriods = parseInt(periods) || 14;
    setSetupData(prev => ({
      ...prev,
      dayPeriods: numPeriods
    }));
    initializeDefaultPatterns();
  };

  const handlePatternChange = (index, field, value) => {
    setSetupData(prev => ({
      ...prev,
      periodPatterns: prev.periodPatterns.map((pattern, i) => 
        i === index ? { ...pattern, [field]: value } : pattern
      )
    }));
  };

  const saveSetupToDatabase = async () => {
    try {
      setLoading(true);
      
      // For now, save to localStorage as a fallback
      const setupDataToSave = {
        ...setupData,
        saved_at: new Date().toISOString()
      };
      
      // Try to save to database first
      try {
        const { error: setupError } = await supabase
          .from('saas_timetable_setup')
          .upsert({
            tenant_id: tenantId,
            mode: setupData.mode,
            day_periods: setupData.dayPeriods,
            period_patterns: setupData.periodPatterns,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (setupError) {
          console.log('Database save failed, using localStorage:', setupError);
          // Fallback to localStorage
          localStorage.setItem('timetable_setup', JSON.stringify(setupDataToSave));
          showMessage('Timetable setup saved to local storage (database not available)', 'success');
        } else {
          showMessage('Timetable setup saved successfully to database!', 'success');
        }
      } catch (dbError) {
        console.log('Database not available, using localStorage:', dbError);
        // Fallback to localStorage
        localStorage.setItem('timetable_setup', JSON.stringify(setupDataToSave));
        showMessage('Timetable setup saved to local storage (database not available)', 'success');
      }
      
      if (onSetupComplete) {
        onSetupComplete(setupData);
      }
      
    } catch (error) {
      console.error('Error saving setup:', error);
      showMessage(`Error saving timetable setup: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingSetup = async () => {
    try {
      setLoading(true);
      
      // Try to load from database first
      try {
        const { data, error } = await supabase
          .from('saas_timetable_setup')
          .select('*')
          .eq('tenant_id', tenantId)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.log('Database load failed, trying localStorage:', error);
          throw error;
        }

        if (data) {
          setSetupData({
            mode: data.mode || 'weekday',
            dayPeriods: data.day_periods || 14,
            periodPatterns: data.period_patterns || []
          });
          showMessage('Existing setup loaded from database!', 'success');
          return;
        }
      } catch (dbError) {
        console.log('Database not available, trying localStorage:', dbError);
      }
      
      // Fallback to localStorage
      const savedSetup = localStorage.getItem('timetable_setup');
      if (savedSetup) {
        try {
          const parsedSetup = JSON.parse(savedSetup);
          setSetupData({
            mode: parsedSetup.mode || 'weekday',
            dayPeriods: parsedSetup.dayPeriods || 14,
            periodPatterns: parsedSetup.periodPatterns || []
          });
          showMessage('Existing setup loaded from local storage!', 'success');
        } catch (parseError) {
          console.error('Error parsing saved setup:', parseError);
          showMessage('Error loading saved setup. Using default configuration.', 'error');
        }
      } else {
        showMessage('No existing setup found. Using default configuration.', 'info');
      }
      
    } catch (error) {
      console.error('Error loading setup:', error);
      showMessage(`Error loading existing setup: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="timetable-setup bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <span className="mr-2">⚙️</span>
        Timetable Setup Configuration
      </h3>
      
      {/* Mode Selection */}
      <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h4 className="font-medium text-gray-700 mb-3">1. Select Timetable Mode</h4>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="mode"
              value="weekday"
              checked={setupData.mode === 'weekday'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mr-2"
            />
            <span className="text-sm">Weekday Mode (Monday-Friday)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="mode"
              value="cycle"
              checked={setupData.mode === 'cycle'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mr-2"
            />
            <span className="text-sm">Cycle Mode (Custom cycle days)</span>
          </label>
        </div>
      </div>

      {/* Day Periods Configuration */}
      <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h4 className="font-medium text-gray-700 mb-3">2. Day Period Pattern</h4>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Number of Day Periods:</label>
          <input
            type="number"
            min="1"
            max="20"
            value={setupData.dayPeriods}
            onChange={(e) => handleDayPeriodsChange(e.target.value)}
            className="w-20 px-3 py-1 border border-gray-300 rounded text-sm"
          />
          <span className="text-sm text-gray-600">(Vertical cells number)</span>
        </div>
      </div>

      {/* Period Patterns Configuration */}
      <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h4 className="font-medium text-gray-700 mb-3">3. Period Pattern Configuration</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Period Sequence</th>
                <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Period Node</th>
                <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Time Slot</th>
              </tr>
            </thead>
            <tbody>
              {setupData.periodPatterns.map((pattern, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-2 py-1 text-sm text-center">
                    {pattern.period_sequence}
                  </td>
                  <td className="border border-gray-300 px-2 py-1">
                    <input
                      type="text"
                      value={pattern.period_node}
                      onChange={(e) => handlePatternChange(index, 'period_node', e.target.value)}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                      placeholder="e.g., Period 1, 小息, 午膳, 班主任節"
                    />
                  </td>
                  <td className="border border-gray-300 px-2 py-1">
                    <input
                      type="text"
                      value={pattern.time_slot}
                      onChange={(e) => handlePatternChange(index, 'time_slot', e.target.value)}
                      className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                      placeholder="e.g., 8:10-8:45"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mb-4">
        <button
          onClick={loadExistingSetup}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Load Existing Setup'}
        </button>
        <button
          onClick={saveSetupToDatabase}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Setup'}
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-3 rounded-lg ${
          messageType === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
          messageType === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
          'bg-blue-100 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">
              {messageType === 'success' ? '✅' : messageType === 'error' ? '❌' : 'ℹ️'}
            </span>
            {message}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">Setup Instructions:</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Mode Selection:</strong> Choose between Weekday (Monday-Friday) or Cycle mode</li>
          <li>• <strong>Day Periods:</strong> Set the number of vertical cells (periods) per day</li>
          <li>• <strong>Period Patterns:</strong> Configure each period with its node name and time slot</li>
          <li>• <strong>Special Periods:</strong> Use "小息" for breaks, "午膳" for lunch, "班主任節" for homeroom</li>
          <li>• <strong>Save Setup:</strong> Save configuration to database for use in timetable management</li>
        </ul>
      </div>
    </div>
  );
};

export default TimetableSetup;
